import AddCommentIcon from '@mui/icons-material/AddComment';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  Badge,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import {
  ImageComment,
  addImageComment,
  createCommentId,
  deleteImageComment,
  getImageComments,
  resolveLoggedInUser,
} from './imageAnnotationStorage';
import {
  FeatureGateName,
  useFeatureGate,
} from '../../../commonUtils/featureSchoolGate';

interface ImageCommentsProps {
  imageUrl: string;
  leading?: ReactNode;
}

export const ImageComments = ({ imageUrl, leading }: ImageCommentsProps) => {
  const annotationsEnabled = useFeatureGate(FeatureGateName.annotationsEnabled);
  const [comments, setComments] = useState<ImageComment[]>([]);
  const [showComments, setShowComments] = useState(false);
  const [commentDialogOpen, setCommentDialogOpen] = useState(false);
  const [draftText, setDraftText] = useState('');

  const resolvedUser = useMemo(() => resolveLoggedInUser(), []);

  useEffect(() => {
    if (imageUrl) {
      setComments(getImageComments(imageUrl));
    } else {
      setComments([]);
    }
  }, [imageUrl]);

  const openAddComment = useCallback(() => {
    setDraftText('');
    setCommentDialogOpen(true);
  }, []);

  const handleSubmitComment = useCallback(() => {
    const text = draftText.trim();
    if (!text || !imageUrl) return;
    const comment: ImageComment = {
      id: createCommentId(),
      userId: resolvedUser.id,
      userName: resolvedUser.name,
      text,
      createdAt: Date.now(),
    };
    setComments(addImageComment(imageUrl, comment));
    setCommentDialogOpen(false);
    setDraftText('');
  }, [draftText, imageUrl, resolvedUser.id, resolvedUser.name]);

  const handleCancelComment = useCallback(() => {
    setCommentDialogOpen(false);
    setDraftText('');
  }, []);

  const handleDeleteComment = useCallback(
    (commentId: string) => {
      setComments(deleteImageComment(imageUrl, commentId));
    },
    [imageUrl]
  );

  if (!imageUrl) return null;

  if (!annotationsEnabled) {
    if (!leading) return null;
    return (
      <Box
        sx={{
          position: 'absolute',
          top: 8,
          right: 8,
          zIndex: 4,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        {leading}
      </Box>
    );
  }

  return (
    <>
      <Box
        sx={{
          position: 'absolute',
          top: 8,
          right: 8,
          zIndex: 4,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        {leading}
        <Box
          sx={{
            display: 'flex',
            gap: 0.5,
            backgroundColor: 'rgba(0,0,0,0.55)',
            borderRadius: 1,
          }}
        >
        <Tooltip title="Add comment">
          <IconButton
            size="small"
            onClick={openAddComment}
            sx={{ color: '#fff' }}
          >
            <AddCommentIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="View comments">
          <IconButton
            size="small"
            onClick={() => setShowComments((prev) => !prev)}
            sx={{ color: '#fff' }}
          >
            <Badge badgeContent={comments.length} color="primary" max={99}>
              <ChatBubbleOutlineIcon fontSize="small" />
            </Badge>
          </IconButton>
        </Tooltip>
        </Box>
      </Box>

      {showComments && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            minHeight: 220,
            minWidth: 240,
            zIndex: 5,
            backgroundColor: 'rgba(20,20,20,0.92)',
            color: '#fff',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            px={1.5}
            py={1}
            sx={{ borderBottom: '1px solid rgba(255,255,255,0.15)' }}
          >
            <Typography variant="subtitle1">Comments</Typography>
            <IconButton
              size="small"
              onClick={() => setShowComments(false)}
              sx={{ color: '#fff' }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
          <Box sx={{ flex: 1, overflowY: 'auto', px: 1.5, py: 1 }}>
            {comments.length === 0 ? (
              <Typography variant="body2" sx={{ opacity: 0.7, mt: 1 }}>
                No comments yet.
              </Typography>
            ) : (
              comments.map((comment) => (
                <Box
                  key={comment.id}
                  sx={{
                    py: 1,
                    borderBottom: '1px solid rgba(255,255,255,0.08)',
                  }}
                >
                  <Box
                    display="flex"
                    alignItems="flex-start"
                    justifyContent="space-between"
                    gap={1}
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ fontSize: '0.875rem' }}>
                        {comment.text}
                      </Typography>
                      {comment.userName && (
                        <Typography
                          variant="caption"
                          display="block"
                          sx={{ opacity: 0.7, mt: 0.25 }}
                        >
                          {comment.userName}
                        </Typography>
                      )}
                    </Box>
                    {resolvedUser.id && comment.userId === resolvedUser.id && (
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteComment(comment.id)}
                        sx={{ color: 'rgba(255,255,255,0.7)' }}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                </Box>
              ))
            )}
          </Box>
        </Box>
      )}

      <Dialog
        open={commentDialogOpen}
        onClose={handleCancelComment}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Add comment</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={2}
            placeholder="Write a comment…"
            value={draftText}
            onChange={(e) => setDraftText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSubmitComment();
              }
            }}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelComment}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSubmitComment}
            disabled={!draftText.trim()}
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
